import React, { useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  Puzzle,
  Settings,
  ClipboardCheck,
  ScrollText,
  MessageSquare,
  Sparkles,
  ChevronLeft,
} from "lucide-react";
import AI_PDF_CONST from "../../util/PdfAiConst";
import useAuth from "../../hooks/useAuth";
import { isPlatformAdmin, isTenantAdmin } from "../../util/accessControl";

const NAV_ICONS = {
  Dashboard: LayoutDashboard,
  Tenants: Building2,
  Team: Users,
  Services: Puzzle,
  Configs: Settings,
  Onboarding: ClipboardCheck,
  "Audit logs": ScrollText,
  "WhatsApp Inbox": MessageSquare,
};

const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileToggle }) => {
  const { user } = useAuth();
  const location = useLocation();
  const currentTab = new URLSearchParams(location.search).get("tab");

  const isItemActive = (item) => {
    const tabMatch = item.to.match(/[?&]tab=([^&]+)/);
    if (tabMatch) {
      return currentTab === tabMatch[1];
    }
    return location.pathname === item.to;
  };

  const role = user?.role || "viewer";
  const myTenantId = user?.tenantId;

  const navItems = useMemo(() => {
    const base = AI_PDF_CONST.ROUTE;
    const detailBase = base.ADMIN_TENANT_DETAIL;
    const platform = isPlatformAdmin(user);

    const detailTab = (tab) =>
      myTenantId
        ? detailBase.replace(":tenant_id", myTenantId) + `?tab=${tab}`
        : base.ADMIN_TENANTS;

    const items = [
      { label: "Dashboard", to: base.ADMIN_DASHBOARD },
      { label: "Tenants", to: platform ? base.ADMIN_TENANTS : detailTab("overview") },
      { label: "Team", to: platform ? base.ADMIN_TEAM : detailTab("team") },
      { label: "Services", to: platform ? base.ADMIN_SERVICES : detailTab("services") },
      { label: "Configs", to: platform ? base.ADMIN_CONFIGS : detailTab("config") },
    ];

    if (isPlatformAdmin(user) || isTenantAdmin(user)) {
      items.push({
        label: "Onboarding",
        to: platform ? base.ADMIN_ONBOARDING : detailTab("onboarding"),
      });
    }

    if (isPlatformAdmin(user) || isTenantAdmin(user)) {
      items.push({
        label: "Audit logs",
        to: platform ? base.ADMIN_AUDIT_LOGS : detailTab("audit"),
      });
    }

    if (isPlatformAdmin(user) || isTenantAdmin(user)) {
      items.push({
        label: "WhatsApp Inbox",
        to: base.ADMIN_WHATSAPP_INBOX,
      });
    }

    return items;
  }, [myTenantId, user]);

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && onMobileToggle && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/60 md:hidden"
          onClick={onMobileToggle}
        />
      )}

      <aside
        className={`${
          collapsed ? "w-[72px]" : "w-[260px]"
        } shrink-0 border-r border-slate-800 bg-[#0f0c20] text-slate-400 min-h-screen flex-col justify-between transition-all duration-200 ${
          mobileOpen ? "fixed inset-y-0 left-0 z-50 flex" : "hidden md:flex"
        }`}
      >
        <div>
          {/* Mobile close button */}
          {mobileOpen && onMobileToggle && (
            <div className="flex justify-end px-3 pt-3 md:hidden">
              <button
                type="button"
                onClick={onMobileToggle}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition"
              >
                <ChevronLeft size={16} />
              </button>
            </div>
          )}

          {/* Brand + toggle */}
          <div className="flex items-center justify-between px-3 pt-5 pb-4">
            <button
              type="button"
              onClick={collapsed ? onToggle : undefined}
              className={`flex items-center gap-3 min-w-0 ${collapsed ? "w-full justify-center" : ""} ${collapsed ? "cursor-pointer rounded-xl p-2 hover:bg-white/5 transition" : ""}`}
              title={collapsed ? "Expand sidebar" : undefined}
            >
              <div className="w-0.5 h-10 bg-cyan-400 shrink-0" />
              <Sparkles size={22} className="text-cyan-400 shrink-0" />
              {!collapsed && (
                <div className="truncate">
                  <h2 className="text-lg font-bold text-white tracking-tight">DocLift</h2>
                  <p className="text-[10px] text-slate-500 mt-0.5">Multi-tenant OS</p>
                </div>
              )}
            </button>
            {onToggle && !collapsed && (
              <button
                type="button"
                onClick={onToggle}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition shrink-0"
              >
                <ChevronLeft size={16} />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className="space-y-1 px-2">
            {navItems.map((item) => {
              const Icon = NAV_ICONS[item.label];
              return (
                <NavLink
                  key={item.label}
                  to={item.to}
                  onClick={mobileOpen && onMobileToggle ? onMobileToggle : undefined}
                  className={() =>
                    `flex items-center rounded-xl px-3 py-3 text-sm font-medium transition-colors ${
                      isItemActive(item)
                        ? "bg-white/10 text-white font-semibold"
                        : "hover:bg-white/5 hover:text-slate-200"
                    } ${collapsed ? "justify-center" : "gap-3"}`
                  }
                  title={collapsed ? item.label : undefined}
                >
                  {Icon && <Icon size={20} className="shrink-0" />}
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* User info */}
        <div className={`border-t border-slate-800/60 pt-4 pb-4 ${collapsed ? "px-0 flex justify-center" : "px-3"}`}>
          {collapsed ? (
            <div className="h-8 w-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <span className="text-xs font-bold text-cyan-400">{role.charAt(0).toUpperCase()}</span>
            </div>
          ) : (
            <>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Signed in as</p>
              <p className="text-xs font-semibold text-slate-300 mt-0.5 capitalize">{role.replace(/_/g, " ")}</p>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
